import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Radio, Activity, MessageSquare, Clock, User, Dumbbell,
  HeartPulse, Zap, CheckCircle, ChevronDown, ChevronUp,
  RefreshCw, Search, MessageCircle, ArrowRight, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SendEmailButton } from '@/components/email/SendEmailButton';
import { useLiveStudio } from '@/hooks/useLiveStudio';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import NoteCard from '@/components/NoteReplyCard';
import { StatusDot } from '@/components/ui/status-dot';

const NOTES_PER_PAGE = 10;
const UNREPLIED_WINDOW_DAYS = 30;

const isAwaitingReply = (note) =>
  (!note.replies || note.replies.length === 0) &&
  (note.triage_status || 'needs_reply') !== 'no_reply_needed';

const isWithinUnrepliedWindow = (note) =>
  Boolean(note.completed_at) &&
  Date.now() - new Date(note.completed_at).getTime() <= UNREPLIED_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffSeconds = Math.round((now - date) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDuration = (startedAt) => {
  if (!startedAt) return '';
  const start = new Date(startedAt);
  const now = new Date();
  const diffMin = Math.round((now - start) / 60000);
  if (diffMin < 60) return `${diffMin}m`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return `${hours}h ${mins}m`;
};

const LiveSessionCard = ({ session, onViewDetails }) => {
  const isActive = session.status === 'in_progress';
  const progress = session.exercises_total > 0
    ? Math.round((session.exercises_completed / session.exercises_total) * 100)
    : 0;

  return (
    <Card className={cn(
      'transition-all',
      isActive && 'border-green-500/50 shadow-green-500/5 shadow-lg'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm',
              isActive ? 'bg-green-600' : 'bg-muted-foreground'
            )}>
              <User className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">{session.full_name || 'Unknown'}</p>
              <div className="flex items-center gap-1">
                <p className="text-xs text-muted-foreground">{session.email}</p>
                <SendEmailButton userId={session.user_id} fullName={session.full_name} email={session.email} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isActive && <StatusDot status="active" />}
            <Badge variant={isActive ? 'default' : 'secondary'} className="text-xs">
              {isActive ? 'Training Now' : session.status}
            </Badge>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Dumbbell className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium truncate">{session.session_name}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {isActive ? formatDuration(session.started_at) : formatRelativeTime(session.started_at)}
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              {session.exercises_completed}/{session.exercises_total} exercises
            </span>
          </div>

          {isActive && session.exercises_total > 0 && (
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {session.latest_note && (
            <div className="mt-2 p-2 bg-muted/50 rounded-md border border-dashed">
              <div className="flex items-start gap-2">
                <StickyNote className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{session.latest_note_exercise}</p>
                  <p className="text-xs italic">"{session.latest_note}"</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 text-xs"
          onClick={() => onViewDetails(session)}
        >
          View Details <ArrowRight className="ml-1 h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
};

const SessionsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[...Array(3)].map((_, i) => (
      <Card key={i}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-36" />
            </div>
          </div>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-1.5 w-full rounded-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const NotesSkeleton = () => (
  <div className="space-y-4">
    {[...Array(4)].map((_, i) => (
      <Card key={i}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-1 flex-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const LiveStudio = () => {
  const navigate = useNavigate();
  const {
    liveSessions,
    recentNotes,
    loadingSessions,
    loadingNotes,
    replyToNote,
    setNoteTriage,
    flagNote,
    resolveNoteFlag,
    refreshSessions,
    refreshNotes,
  } = useLiveStudio();
  const { user, isAdmin } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [notesFilter, setNotesFilter] = useState('all');
  const [notesPage, setNotesPage] = useState(1);

  const activeSessions = useMemo(
    () => liveSessions.filter(s => s.status === 'in_progress'),
    [liveSessions]
  );

  const completedSessions = useMemo(
    () => liveSessions.filter(s => s.status !== 'in_progress'),
    [liveSessions]
  );

  const filteredNotes = useMemo(() => {
    let notes = recentNotes;
    if (notesFilter === 'unreplied') {
      notes = notes.filter(n => isAwaitingReply(n) && isWithinUnrepliedWindow(n));
    } else if (notesFilter === 'older_unreplied') {
      notes = notes.filter(n => isAwaitingReply(n) && !isWithinUnrepliedWindow(n));
    } else if (notesFilter === 'replied') {
      notes = notes.filter(n => n.replies && n.replies.length > 0);
    } else if (notesFilter === 'flagged') {
      notes = notes.filter(n => n.flag);
    } else if (notesFilter === 'pain') {
      notes = notes.filter(n => n.pain_level_reported && n.pain_level_reported > 0);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      notes = notes.filter(n =>
        n.full_name?.toLowerCase().includes(term) ||
        n.exercise_name?.toLowerCase().includes(term) ||
        n.notes?.toLowerCase().includes(term)
      );
    }
    return notes;
  }, [recentNotes, notesFilter, searchTerm]);

  const unrepliedCount = useMemo(
    () => recentNotes.filter(n => isAwaitingReply(n) && isWithinUnrepliedWindow(n)).length,
    [recentNotes]
  );

  const flaggedCount = useMemo(() => recentNotes.filter(n => n.flag).length, [recentNotes]);

  const totalNotesPages = Math.max(1, Math.ceil(filteredNotes.length / NOTES_PER_PAGE));
  const paginatedNotes = useMemo(() => {
    const start = (notesPage - 1) * NOTES_PER_PAGE;
    return filteredNotes.slice(start, start + NOTES_PER_PAGE);
  }, [filteredNotes, notesPage]);
  const firstNoteNumber = filteredNotes.length === 0 ? 0 : (notesPage - 1) * NOTES_PER_PAGE + 1;
  const lastNoteNumber = Math.min(notesPage * NOTES_PER_PAGE, filteredNotes.length);

  useEffect(() => {
    setNotesPage(1);
  }, [searchTerm, notesFilter]);

  useEffect(() => {
    setNotesPage(page => Math.min(page, totalNotesPages));
  }, [totalNotesPages]);

  const handleViewDetails = (session) => {
    navigate(`/user-activity/${session.user_id}/session/${session.training_session_id}`);
  };

  const handleNoteTriage = async (completionId, status) => {
    try {
      await setNoteTriage(completionId, status);
      toast.success(status === 'no_reply_needed' ? 'Marked as no reply needed' : 'Marked as needs reply');
    } catch (err) {
      toast.error('Failed to update note status', { description: err.message });
    }
  };

  return (
    <>
      <Helmet>
        <title>Live Studio - My Movement Space</title>
      </Helmet>

      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">Live Studio</h1>
            {activeSessions.length > 0 && (
              <Badge variant="default" className="animate-pulse">
                <Radio className="h-3 w-3 mr-1" />
                {activeSessions.length} Live
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor training sessions and respond to athlete notes in real time.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { refreshSessions(); refreshNotes(); }}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="space-y-8">
        {/* Active Sessions */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-green-500" />
            <h2 className="text-lg font-medium">Active Sessions</h2>
            <Badge variant="outline" className="ml-1">{activeSessions.length}</Badge>
          </div>

          {loadingSessions ? (
            <SessionsSkeleton />
          ) : activeSessions.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-32 gap-2">
                <Radio className="h-6 w-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No active training sessions right now.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeSessions.map(session => (
                <LiveSessionCard
                  key={session.training_session_id}
                  session={session}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </section>

        {/* Recent Completed Sessions */}
        {completedSessions.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-medium">Recent Sessions (24h)</h2>
              <Badge variant="outline" className="ml-1">{completedSessions.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedSessions.slice(0, 6).map(session => (
                <LiveSessionCard
                  key={session.training_session_id}
                  session={session}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </section>
        )}

        {/* Notes Feed */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-medium">Athlete Notes</h2>
              {unrepliedCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {unrepliedCount} unreplied
                </Badge>
              )}
              {flaggedCount > 0 && (
                <Badge
                  variant="destructive"
                  className="ml-1 cursor-pointer"
                  onClick={() => setNotesFilter('flagged')}
                >
                  {flaggedCount} flagged
                </Badge>
              )}
            </div>
          </div>

          <Card className="mb-4">
            <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search notes by athlete, exercise, or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'unreplied', label: `Unreplied (${UNREPLIED_WINDOW_DAYS}d)` },
                  { value: 'older_unreplied', label: 'Older unreplied' },
                  { value: 'replied', label: 'Replied' },
                  { value: 'flagged', label: 'Flagged' },
                  { value: 'pain', label: 'With Pain' },
                ].map(filter => (
                  <Button
                    key={filter.value}
                    variant={notesFilter === filter.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNotesFilter(filter.value)}
                    className="text-xs"
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {loadingNotes ? (
            <NotesSkeleton />
          ) : filteredNotes.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-32 gap-2">
                <MessageSquare className="h-6 w-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {searchTerm || notesFilter !== 'all'
                    ? 'No notes match your filters.'
                    : 'No athlete notes yet.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {paginatedNotes.map(note => (
                <NoteCard
                  key={note.completion_id}
                  note={note}
                  onReply={replyToNote}
                  onTriage={handleNoteTriage}
                  onFlag={flagNote}
                  onResolveFlag={resolveNoteFlag}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                />
              ))}
              {filteredNotes.length > NOTES_PER_PAGE && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Showing {firstNoteNumber}-{lastNoteNumber} of {filteredNotes.length} notes
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setNotesPage(page => Math.max(1, page - 1))}
                      disabled={notesPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {notesPage} of {totalNotesPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setNotesPage(page => Math.min(totalNotesPages, page + 1))}
                      disabled={notesPage === totalNotesPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default LiveStudio;
